/**
 * rc129 — Marca del ISP para el panel admin.
 *
 * `useBranding()` lee GET /api/v1/branding (público): nombre comercial, logo y
 * color. `useMarcaEmpresa(empresaId)` da el nombre/razón social de una empresa
 * concreta (para tarjetas de pago/WiFi del cliente) y cae a la marca global.
 * Antes todo esto era "BYTECOM"/"Bytecom" escrito a mano.
 */
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

export type Branding = {
  nombre: string; logo_url: string | null; color_primary: string | null; timezone?: string;
  /** rc147 — dominios de esta instalación (.env); null en instalaciones viejas */
  dominio_admin?: string | null; dominio_portal?: string | null;
};

/** rc147 — "io.bytecom.mx" → "bytecom.mx". Sin dominio usa el host actual. */
export function apexDe(dominio?: string | null): string {
  const h = (dominio || (typeof window !== "undefined" ? window.location.hostname : "") || "").trim().toLowerCase();
  const partes = h.split(".").filter(Boolean);
  if (partes.length > 2) return partes.slice(1).join(".");
  return h.includes(".") ? h : "tuisp.mx";
}

/** rc147 — dominios de la instalación para textos y ejemplos (antes "bytecom.mx" fijo). */
export function useDominios() {
  const b = useBranding().data;
  const admin = b?.dominio_admin || (typeof window !== "undefined" ? window.location.hostname : "");
  const apex = apexDe(admin);
  const portal = b?.dominio_portal || `mi.${apex}`;
  return { admin, portal, apex };
}
type EmpresaMin = { id: number; nombre: string; razon_social?: string | null; activa?: boolean };

export function useBranding() {
  return useQuery<Branding>({
    queryKey: ["branding"],
    queryFn: () => apiCall<Branding>("get", "/api/v1/branding"),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

/** Marca con la que se presenta un cliente: su empresa; si no, el ISP. */
export function useMarcaEmpresa(empresaId: number | null | undefined, enabled = true) {
  const brand = useBranding();
  const empresas = useQuery<EmpresaMin[]>({
    queryKey: ["empresas"],
    queryFn: () => apiCall<EmpresaMin[]>("get", "/api/v1/empresas"),
    staleTime: 5 * 60 * 1000,
    retry: 0,
    enabled: enabled && empresaId != null,
  });
  const emp = empresas.data?.find((e) => e.id === empresaId);
  const nombre = (emp?.nombre || brand.data?.nombre || "").trim();
  const razonSocial = (emp?.razon_social || "").trim() || nombre;
  return { nombre, razonSocial, empresa: emp ?? null, isLoading: brand.isLoading || empresas.isLoading };
}
